import "./DocumentoEditorBase.css";
import { previousDetailLines } from "./previousDetailLines";
import Spinner from "../../shared/UxbiSpinner";
import CurrencyFields from "../../modules/ventas/CurrencyFields";
import {
  canUsePrevious,
  previousCurrency,
} from "../../modules/ventas/documentRules";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import { Building2, CreditCard, FileText, MapPin, Package, EllipsisVertical, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import RemoteSelect from "../common/RemoteSelect";
import DocumentoPrevioSelector from "./DocumentoPrevioSelector";
import { getCurrentUser } from "../utils/getCurrentUser";
import {
  listarMonedasDocumento,
  listarConceptosPresupuesto,
  obtenerConceptoPresupuesto,
  actualizarDocumento,
  crearDocumento,
  listarCatalogoClientes,
  listarCatalogoProductos,
  listarAlmacenes,
  listarConfiguracionesDocumento,
  listarDirecciones,
  listarEmpresas,
  listarPreciosEspeciales,
  listarVehiculos,
  listarFormasPagoSat,
  listarMetodosPagoSat,
  listarUsosComprobanteSat,
  obtenerAlmacen,
  obtenerClienteDocumento,
  obtenerDireccion,
  obtenerEmpresa,
  obtenerPrecioEspecial,
  obtenerProductoDocumento,
  obtenerTipoDescuento,
  obtenerVehiculo,
} from "../../api/documentos";
import {
  addressName,
  buildDetailPayload,
  calculateTotals,
  documentProductPrice,
  emptyHeader,
  extractCollection,
  money,
  productName,
} from "./documentoUtils";

const nullableInt = (value) =>
  value === "" || value == null ? null : Number(value);
const isTrue = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";
const getDocumentKey = (value) =>
  String(
    value?.documento?.clave ||
      value?.documento?.clave_documento ||
      value?.clave ||
      value?.clave_documento ||
      value?.documento_clave ||
      "",
  )
    .trim()
    .toUpperCase();

export default function DocumentoEditorBase({
  config,
  show,
  onHide,
  onSaved,
  record = null,
  readOnly = false,
}) {
  const [header, setHeader] = useState(emptyHeader);
  const [lines, setLines] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  useEffect(() => {
    if (show) listarMonedasDocumento().then((response) => setCurrencies(extractCollection(response))).catch((err) => toast.error(err.message));
  }, [show]);
  const [configuration, setConfiguration] = useState(null);
  const [relatedHeaders, setRelatedHeaders] = useState([]);
  const [selectingPrevious, setSelectingPrevious] = useState(false);
  const [previousSelectorRequired, setPreviousSelectorRequired] =
    useState(false);
  const [discountType, setDiscountType] = useState(1);
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [satCatalogs, setSatCatalogs] = useState({
    formas: [],
    metodos: [],
    usos: [],
  });
  const user = getCurrentUser();
  const adjective = (masculine, feminine) =>
    config.genero === "f" ? feminine : masculine;

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    setRelatedHeaders([]);
    setSelectingPrevious(false);
    setPreviousSelectorRequired(false);
    const currentCfdi = Array.isArray(record?.cfdi_documentos)
      ? record.cfdi_documentos[0]
      : record?.cfdi_documentos ||
        record?.cfdi_documento ||
        record?.cfdi?.[0] ||
        {};
    const initial = record
      ? {
          ...emptyHeader(),
          ...record,
          id_empresa:
            record.id_empresa || record.empresa?.id || user.id_empresa || "",
          id_cliente: record.id_cliente || record.cliente?.id || "",
          id_direccion:
            record.id_direccion ||
            record.direcciones?.id ||
            record.direccion?.id ||
            "",
          id_precio_especial:
            record.id_precio_especial || record.precio_especial?.id || "",
          fecha: String(record.fecha || "").slice(0, 10),
          fecha_entrega: String(record.fecha_entrega || "").slice(0, 10),
          fecha_vencimiento: String(record.fecha_vencimiento || "").slice(
            0,
            10,
          ),
          fecha_revision: String(record.fecha_revision || "").slice(0, 10),
          forma_pago: currentCfdi.forma_pago || "",
          metodo_pago: currentCfdi.metodo_pago || "",
          uso_comprobante: currentCfdi.uso_comprobante || "",
          lugar_expedicion:
            currentCfdi.lugar_expedicion || record.lugar_expedicion || "",
          serie_factura:
            currentCfdi.serie_factura || record.serie_factura || "",
        }
      : {
          ...emptyHeader(),
          id_empresa: user.id_empresa || "",
          nombre_solicita: user.nombre || "",
        };
    setHeader(initial);
    setLines(
      (record?.detalles || []).map((detail) => ({
        ...detail,
        _key: `${detail.id || "new"}-${Math.random()}`,
      })),
    );

    Promise.all([
      listarConfiguracionesDocumento({
        limit: 100,
        filters: [
          ...(config.idDocumento
            ? [{ property: "id", operator: "=", value: config.idDocumento }]
            : []),
          {
            property: "clave_documento_tipo",
            operator: "=",
            value: config.claveTipo,
            relation: "documentoTipo",
          },
        ],
      }),
      obtenerTipoDescuento().catch(() => ({ tipo: 1 })),
    ])
      .then(([response, discountResponse]) => {
        const records = extractCollection(response);
        const selected =
          records.find(
            (item) => String(item.id) === String(config.idDocumento),
          ) ||
          records.find(
            (item) =>
              config.claveDocumento &&
              getDocumentKey(item) ===
                String(config.claveDocumento).toUpperCase(),
          ) ||
          records.find(
            (item) =>
              item.documento_tipo?.clave_documento_tipo === config.claveTipo,
          ) ||
          records[0];
        if (!selected)
          throw new Error(
            `No se encontró la configuración del documento ${config.claveTipo}.`,
          );
        setConfiguration(selected);
        if (!record && isTrue(selected.requiere_previo)) {
          setPreviousSelectorRequired(true);
          setSelectingPrevious(true);
        }
        setDiscountType(
          Number(discountResponse?.tipo ?? discountResponse?.data?.tipo ?? 1),
        );
        if (!record && !user.id_empresa && selected.id_empresa) {
          setHeader((current) => ({
            ...current,
            id_empresa: selected.id_empresa,
          }));
        }
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [config.claveDocumento, config.claveTipo, record, show]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!show || !config.esFactura) return;
    Promise.all([
      listarFormasPagoSat({ limit: 1000 }),
      listarMetodosPagoSat({ limit: 1000 }),
      listarUsosComprobanteSat({ limit: 1000 }),
    ])
      .then(([formas, metodos, usos]) =>
        setSatCatalogs({
          formas: extractCollection(formas),
          metodos: extractCollection(metodos),
          usos: extractCollection(usos),
        }),
      )
      .catch((err) =>
        toast.error(`No fue posible cargar los catálogos SAT: ${err.message}`),
      );
  }, [config.esFactura, show]);

  useEffect(() => {
    if (!show || !config.esFactura) return;
    const requests = [
      header.id_empresa
        ? obtenerEmpresa(header.id_empresa)
        : Promise.resolve(null),
      header.id_cliente
        ? obtenerClienteDocumento(header.id_cliente)
        : Promise.resolve(null),
    ];
    Promise.all(requests)
      .then(([companyResponse, clientResponse]) => {
        const company =
          companyResponse?.data?.data ||
          companyResponse?.data ||
          companyResponse;
        const client =
          clientResponse?.data?.data || clientResponse?.data || clientResponse;
        const fiscal =
          company?.dato_facturacion || company?.datos_facturacion || {};
        setHeader((current) => ({
          ...current,
          razon_social_emisor:
            fiscal.razon_social ||
            company?.razon_social ||
            company?.nombre ||
            current.razon_social_emisor,
          rfc_emisor: fiscal.rfc || company?.rfc || current.rfc_emisor,
          regimen_fiscal_emisor:
            fiscal.regimen_fiscal_sat ||
            fiscal.regimen_fiscal ||
            current.regimen_fiscal_emisor,
          lugar_expedicion:
            current.lugar_expedicion ||
            fiscal.direccion?.codigo_postal ||
            company?.direccion?.codigo_postal ||
            "",
          razon_social_receptor:
            client?.razon_social ||
            client?.nombre ||
            current.razon_social_receptor,
          rfc_receptor: client?.rfc || current.rfc_receptor,
          regimen_fiscal_receptor:
            client?.regimen_fiscal_sat ||
            client?.regimen_fiscal ||
            current.regimen_fiscal_receptor,
        }));
      })
      .catch((err) =>
        toast.error(`No fue posible cargar los datos fiscales: ${err.message}`),
      );
  }, [config.esFactura, header.id_cliente, header.id_empresa, show]);

  useEffect(() => {
    if (!config.esFactura || !header.fecha) return;
    const date = new Date(`${header.fecha}T00:00:00`);
    date.setDate(date.getDate() + Number(header.plazo_dias || 0));
    const dueDate = Number.isNaN(date.getTime())
      ? ""
      : date.toISOString().slice(0, 10);
    setHeader((current) =>
      current.fecha_vencimiento === dueDate
        ? current
        : { ...current, fecha_vencimiento: dueDate },
    );
  }, [config.esFactura, header.fecha, header.plazo_dias]);

  const ivaPct = Number(
    configuration?.iva_porcentual ?? configuration?.iva ?? 0,
  );
  const totals = useMemo(
    () => calculateTotals(lines, ivaPct, header.descuento_pct, discountType),
    [discountType, header.descuento_pct, ivaPct, lines],
  );

  const setField = (name, value) =>
    setHeader((current) => ({ ...current, [name]: value }));

  const changeDocumentCurrency = (id, rate) => {
    const oldRate = Number(header.tipo_cambio);
    const newRate = Number(rate);
    if (lines.length && (!(oldRate > 0) || !(newRate > 0))) {
      toast.error("Configura un tipo de cambio válido antes de cambiar la moneda.");
      return;
    }
    if (lines.length) setLines((current) => current.map((line) => ({
      ...line, precio: Number((Number(line.precio) * oldRate / newRate).toFixed(6)),
    })));
    setHeader((current) => ({...current, id_moneda: id, tipo_cambio: rate}));
  };

  const setAsDoctoPosterior = async (selection) => {
    const previousDocuments = Array.isArray(selection)
      ? selection
      : [selection];
    const previousDocument = previousDocuments[0];
    const previousMoney = previousCurrency(previousDocuments);
    const companyIds = new Set(
      previousDocuments.map((documento) =>
        String(documento.id_empresa || documento.empresa?.id || ""),
      ),
    );
    const clientIds = new Set(
      previousDocuments.map((documento) =>
        String(documento.id_cliente || documento.cliente?.id || ""),
      ),
    );
    if (companyIds.size > 1 || clientIds.size > 1) {
      throw new Error(
        "Los documentos previos seleccionados deben pertenecer a la misma empresa y al mismo cliente.",
      );
    }
    const details = previousDocuments.flatMap((documento) =>
      (documento.detalles || []).map((detail) => ({
        ...detail,
        _id_documento_previo: documento.id,
      })),
    );
    const availableLines = previousDetailLines(details);

    if (!availableLines.length) {
      throw new Error(
        "El documento seleccionado ya no tiene partidas con saldo pendiente.",
      );
    }

    const previousCompanyId =
      previousDocument.id_empresa || previousDocument.empresa?.id || "";
    const previousClientId =
      previousDocument.id_cliente || previousDocument.cliente?.id || "";
    const previousDirectionId =
      previousDocument.id_direccion ||
      previousDocument.direcciones?.id ||
      previousDocument.direccion?.id ||
      "";
    const previousPriceId =
      previousDocument.id_precio_especial ||
      previousDocument.precio_especial?.id ||
      "";
    const nextStatus =
      (previousDocument.documento?.posteriores || []).find(
        (posterior) =>
          String(
            posterior.clave ||
              posterior.documento_tipo?.clave_documento_tipo ||
              "",
          ).toUpperCase() === String(config.claveTipo).toUpperCase(),
      )?.pivot?.estatus_nuevodocto || "N";

    setHeader((current) => ({
      ...current,
      id_empresa: previousCompanyId,
      id_cliente: previousClientId,
      id_direccion: previousDirectionId,
      direccion_envio: previousDirectionId,
      id_precio_especial: previousPriceId,
      referencia: previousDocument.referencia || "",
      orden_compra: previousDocument.orden_compra || "",
      tipo_envio: previousDocument.tipo_envio || current.tipo_envio,
      fecha_entrega: String(previousDocument.fecha_entrega || "").slice(0, 10),
      horario_entrega: previousDocument.horario_entrega || "",
      comentarios: previousDocument.comentarios || "",
      descuento_pct: Number(previousDocument.descuento_pct || 0),
      id_moneda: previousMoney.id_moneda,
      tipo_cambio: previousMoney.tipo_cambio,
      plazo_dias: Number(previousDocument.plazo_dias || 0),
      condiciones_pago:
        previousDocument.condiciones_pago || current.condiciones_pago,
      fecha_vencimiento: String(previousDocument.fecha_vencimiento || "").slice(
        0,
        10,
      ),
      estatus: nextStatus,
    }));
    setLines(availableLines);
    setRelatedHeaders(previousDocuments);
    setPreviousSelectorRequired(false);
    setSelectingPrevious(false);
  };

  const openPreviousSelector = () => {
    setPreviousSelectorRequired(false);
    setSelectingPrevious(true);
  };

  const closePreviousSelector = () => {
    if (previousSelectorRequired) {
      onHide();
      return;
    }
    setSelectingPrevious(false);
  };
  const loadCompanies = useCallback(
    ({ search, page, limit }) =>
      listarEmpresas({
        page,
        limit,
        filters: search
          ? [{ property: "nombre", operator: "like", value: search }]
          : [],
        sort: [{ property: "nombre", direction: "ASC" }],
      }),
    [],
  );
  const loadClients = useCallback(
    ({ search, page, limit }) =>
      listarCatalogoClientes({
        page,
        limit,
        filters: [
          ...(config.esNotaVenta ? [{ property: "cliente_prospecto", operator: "=", value: 1, condition: "AND" }] : []),
          ...(header.id_empresa
            ? [
                {
                  property: "id_empresa",
                  operator: "=",
                  value: Number(header.id_empresa),
                  condition: "AND",
                },
              ]
            : []),
          ...(search
            ? [
                {
                  property: "nombre",
                  operator: "like",
                  value: search,
                  condition: "AND",
                },
              ]
            : []),
        ],
        sort: [{ property: "nombre", direction: "ASC" }],
      }),
    [header.id_empresa, config.esNotaVenta],
  );
  const loadProducts = useCallback(
    ({ search, page, limit }) =>
      listarCatalogoProductos({
        page,
        limit,
        filters: [
          ...(search
            ? [
                {
                  property: "descripcion",
                  operator: "like",
                  value: search,
                  condition: "AND",
                },
              ]
            : []),
        ],
        sort: [{ property: "descripcion", direction: "ASC" }],
      }),
    [],
  );
  const loadPrices = useCallback(
    ({ search, page, limit }) =>
      listarPreciosEspeciales({
        page,
        limit,
        filters: search
          ? [{ property: "nombre", operator: "like", value: search }]
          : [],
        sort: [{ property: "nombre", direction: "ASC" }],
      }),
    [],
  );
  const loadWarehouses = useCallback(
    ({ search, page, limit }) =>
      listarAlmacenes({
        page,
        limit,
        filters: [
          ...(header.id_empresa
            ? [
                {
                  property: "id_empresa",
                  operator: "=",
                  value: Number(header.id_empresa),
                  condition: "AND",
                },
              ]
            : []),
          ...(search
            ? [
                {
                  property: "nombre",
                  operator: "like",
                  value: search,
                  condition: "AND",
                },
              ]
            : []),
        ],
        sort: [{ property: "nombre", direction: "ASC" }],
      }),
    [header.id_empresa],
  );
  const loadVehicles = useCallback(
    ({ search, page, limit }) =>
      listarVehiculos({
        page,
        limit,
        filters: search
          ? [{ property: "nombre", operator: "like", value: search }]
          : [],
        sort: [{ property: "nombre", direction: "ASC" }],
      }),
    [],
  );

  const directionFilters = useMemo(() => {
    if (header.tipo_envio === "Cliente recoge" && header.id_empresa) {
      return [
        {
          property: "id",
          operator: "=",
          value: Number(header._empresa_direccion_id || 0),
        },
      ];
    }
    if (header.tipo_envio === "A Domicilio" && header.id_cliente) {
      return [
        {
          property: "id_cliente",
          relation: "clientesDirecciones",
          operator: "=",
          value: Number(header.id_cliente),
          condition: "AND",
        },
        { property: "tipo", operator: "=", value: 2, condition: "AND" },
      ];
    }
    return [];
  }, [
    header._empresa_direccion_id,
    header.id_cliente,
    header.id_empresa,
    header.tipo_envio,
  ]);

  const loadDirections = useCallback(
    ({ search, page, limit }) =>
      listarDirecciones({
        page,
        limit,
        filters: directionFilters,
        sort: [{ property: "calle", direction: "ASC" }],
      }),
    [directionFilters],
  );

  const selectFirstDirection = useCallback(
    async (tipoEnvio, empresaId, clienteId, empresaDireccionId) => {
      if (!tipoEnvio) return;
      let filters = [];
      if (tipoEnvio === "Cliente recoge") {
        if (!empresaId) return;
        let directionId = empresaDireccionId;
        if (!directionId) {
          const response = await obtenerEmpresa(empresaId);
          const company = response?.data?.data || response?.data || response;
          directionId = company.id_direccion || company.direccion?.id;
          setHeader((current) => ({
            ...current,
            _empresa_direccion_id: directionId || "",
          }));
        }
        if (!directionId)
          throw new Error(
            "La empresa seleccionada no tiene una dirección configurada.",
          );
        filters = [
          { property: "id", operator: "=", value: Number(directionId) },
        ];
      } else if (tipoEnvio === "A Domicilio") {
        if (!clienteId) return;
        filters = [
          {
            property: "id_cliente",
            relation: "clientesDirecciones",
            operator: "=",
            value: Number(clienteId),
            condition: "AND",
          },
          { property: "tipo", operator: "=", value: 2, condition: "AND" },
        ];
      }
      const response = await listarDirecciones({
        page: 1,
        limit: 50,
        filters,
        sort: [{ property: "id", direction: "ASC" }],
      });
      const addresses = extractCollection(response);
      if (!addresses.length) {
        const owner =
          tipoEnvio === "Cliente recoge" ? "la empresa" : "el cliente";
        throw new Error(
          `No hay una dirección de envío configurada para ${owner}.`,
        );
      }
      setHeader((current) => ({
        ...current,
        id_direccion: addresses[0].id,
        direccion_envio: addressName(addresses[0]),
      }));
    },
    [],
  );

  const handleCompanyChange = async (_name, value) => {
    setProductId("");
    setLines([]);
    setHeader((current) => ({
      ...current,
      id_empresa: value,
      id_cliente: "",
      id_precio_especial: "",
      id_almacen: "",
      id_vehiculos: "",
      tipo_envio: "",
      id_direccion: "",
      direccion_envio: "",
      _empresa_direccion_id: "",
      razon_social_receptor: "",
      rfc_receptor: "",
      regimen_fiscal_receptor: "",
    }));
    if (value && config.esFactura) {
      try {
        const response = await obtenerEmpresa(value);
        const company = response?.data?.data || response?.data || response;
        const fiscal =
          company?.dato_facturacion || company?.datos_facturacion || {};
        setHeader((current) => ({
          ...current,
          razon_social_emisor:
            fiscal.razon_social || company.razon_social || company.nombre || "",
          rfc_emisor: fiscal.rfc || company.rfc || "",
          regimen_fiscal_emisor:
            fiscal.regimen_fiscal_sat || fiscal.regimen_fiscal || "",
          lugar_expedicion:
            current.lugar_expedicion ||
            fiscal.direccion?.codigo_postal ||
            company.direccion?.codigo_postal ||
            "",
        }));
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const handleClientChange = async (_name, value) => {
    const tipoEnvio = value
      ? config.claveTipo === "PVTA"
        ? "A Domicilio"
        : "Cliente recoge"
      : "";
    setHeader((current) => ({
      ...current,
      id_cliente: value,
      tipo_envio: tipoEnvio,
      id_direccion: "",
      direccion_envio: "",
    }));
    if (value) {
      try {
        await selectFirstDirection(
          tipoEnvio,
          header.id_empresa,
          value,
          header._empresa_direccion_id,
        );
        if (config.esFactura) {
          const response = await obtenerClienteDocumento(value);
          const client = response?.data?.data || response?.data || response;
          setHeader((current) => ({
            ...current,
            plazo_dias: Number(client.dias_credito || 0),
            condiciones_pago:
              Number(client.dias_credito || 0) > 0
                ? `Pago a ${Number(client.dias_credito)} días.`
                : "Pago de contado.",
            razon_social_receptor: client.razon_social || client.nombre || "",
            rfc_receptor: client.rfc || "",
            regimen_fiscal_receptor:
              client.regimen_fiscal_sat || client.regimen_fiscal || "",
          }));
        }
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const handleShippingChange = async (value) => {
    setHeader((current) => ({
      ...current,
      tipo_envio: value,
      id_direccion: "",
      direccion_envio: "",
    }));
    if (!value) return;
    try {
      await selectFirstDirection(
        value,
        header.id_empresa,
        header.id_cliente,
        header._empresa_direccion_id,
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePriceListChange = (_name, value) => {
    try {
      const updated = lines.map((line) => {
        if (!line.producto) return line;
        return { ...line, precio: documentProductPrice(line.producto, { ...header, id_precio_especial: value }, currencies) };
      });
      setHeader((current) => ({ ...current, id_precio_especial: value }));
      setLines(updated);
    } catch (err) { toast.error(err.message); }
  };

  const addProduct = async () => {
    if (!header.id_cliente) return toast.info("Selecciona un cliente primero.");
    if (!productId) return toast.info("Selecciona un producto.");
    try {
      const response = await obtenerProductoDocumento(productId);
      const product = response?.data?.data || response?.data || response;
      const presentation =
        product.presentaciones?.[0] ||
        product.presentaciones_productos?.[0] ||
        {};
      const price = documentProductPrice(product, header, currencies);
      setLines((current) => [
        ...current,
        {
          _key: `new-${Date.now()}`,
          id_producto: product.id,
          producto: product,
          descripcion: productName(product),
          cantidad: 1,
          cantidad2: 1,
          precio: price,
          ...Object.fromEntries(["iva", "ieps", "impuestos_adicionales", "retenciones", "retenciones_iva"].map((tax) => [`${tax}_pct`, Number(product[tax] ?? (tax === "iva" ? ivaPct : 0))])),
          descuento_pct: 0,
          descuento: 0,
          id_productos_unidad_medida:
            product.id_productos_unidad_medida ||
            presentation.id_productos_unidad_medida ||
            null,
          nombre_prod_unidad_medida:
            product.unidad_medida?.nombre ||
            product.unidad_medida?.descripcion ||
            presentation.nombre_prod_unidad_medida ||
            "",
        },
      ]);
      setProductId("");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const updateLine = (key, name, value) =>
    setLines((current) =>
      current.map((line) =>
        line._key === key
          ? { ...line, [name]: Math.max(0, Number(value)) }
          : line,
      ),
    );

  const validate = () => {
    if (
      !header.id_moneda ||
      !Number.isFinite(Number(header.tipo_cambio)) ||
      Number(header.tipo_cambio) <= 0
    )
      return "Selecciona moneda y un tipo de cambio válido.";
    if (!header.id_cliente) return "Selecciona un cliente.";
    if (!header.id_empresa) return "Selecciona una empresa.";
    if (!header.fecha) return "Captura la fecha.";
    if (config.requiereAlmacen && !header.id_almacen)
      return "Selecciona un almacén.";
    if (!totals.lines.length) return "Añade al menos un producto.";
    if (totals.lines.some((line) => Number(line.cantidad2) <= 0))
      return "Todas las cantidades deben ser mayores a cero.";
    if (
      config.esFactura &&
      totals.lines.some((line) => Number(line.importe) <= 0)
    )
      return "Las partidas de una factura deben tener un importe mayor a cero.";
    if (config.esFactura && !header.condiciones_pago)
      return "Captura las condiciones de pago.";
    if (
      config.esFactura &&
      (!header.forma_pago || !header.metodo_pago || !header.uso_comprobante)
    )
      return "Completa forma de pago, método de pago y uso de CFDI.";
    return "";
  };

  const getPedidoStatus = async () => record?.estatus ?? header.estatus ?? "N";

  const save = async () => {
    const validation = validate();
    if (validation) {
      toast.error(validation);
      return;
    }
    setSaving(true);
    let documentStatus;
    try {
      if (config.esFactura && !record?.id && relatedHeaders.length === 0) {
        const response = await obtenerClienteDocumento(header.id_cliente);
        const client = response?.data?.data || response?.data || response;
        if (isTrue(client?.bloqueo_factura))
          throw new Error(
            "El cliente seleccionado tiene bloqueada la creación de facturas.",
          );
      }
      documentStatus = await getPedidoStatus();
    } catch (err) {
      setSaving(false);
      return toast.error(
        `No fue posible validar el crédito del cliente: ${err.message}`,
      );
    }
    const documentType = configuration.documento_tipo || {};
    const documentGroup = configuration.documento_grupo || {};
    const payload = {
      config: {
        afecta_cxp_cxc: configuration.afecta_cxp_cxc,
        afecta_inventarios: configuration.afecta_inventarios,
        afecta_est_venta: configuration.afecta_est_venta,
        afecta_est_devolucion: configuration.afecta_est_devolucion,
        requiere_previo: configuration.requiere_previo,
        doctos_relacionados: record?.id ? [] : JSON.stringify(relatedHeaders),
        es_devolucion: false,
        es_produccion: false,
      },
      documento: {
        encabezado: {
          id: nullableInt(record?.id),
          id_documento: configuration.id,
          id_documentos_tipo:
            configuration.id_documentos_tipo || documentType.id,
          id_documentos_grupo:
            configuration.id_documentos_grupo || documentGroup.id,
          id_empresa: nullableInt(header.id_empresa),
          id_usuario: nullableInt(user.id),
          id_cliente: nullableInt(header.id_cliente),
          id_precio_especial: nullableInt(header.id_precio_especial),
          id_concepto_presupuesto: nullableInt(header.id_concepto_presupuesto),
          id_almacen: nullableInt(header.id_almacen),
          id_vehiculos: nullableInt(header.id_vehiculos),
          id_moneda: nullableInt(
            header.id_moneda ||
              configuration.id_moneda ||
              configuration.id_moneda_original,
          ),
          fecha: header.fecha,
          fecha_entrega: header.fecha_entrega || null,
          referencia: header.referencia || null,
          orden_compra: header.orden_compra || null,
          nombre_solicita: header.nombre_solicita || null,
          tipo_envio: header.tipo_envio || null,
          id_direccion: nullableInt(header.id_direccion),
          direccion_envio: nullableInt(header.id_direccion),
          horario_entrega: header.horario_entrega || null,
          plazo_dias: Number(header.plazo_dias || 0),
          condiciones_pago: header.condiciones_pago || null,
          fecha_vencimiento: header.fecha_vencimiento || null,
          fecha_revision: header.fecha_revision || null,
          comentarios: header.comentarios || null,
          tipo_cambio: Number(header.tipo_cambio || 1),
          descuento_pct: Number(header.descuento_pct || 0),
          descuento: totals.descuento,
          subtotal: totals.subtotal,
          saldo_documento: totals.subtotal,
          iva: totals.iva,
          ieps_isr: totals.ieps,
          impuestos_adicionales: totals.impuestos_adicionales,
          retenciones: totals.retenciones,
          retenciones_iva: totals.retenciones_iva,
          importe: totals.importe,
          estatus: documentStatus,
        },
        cfdi_documentos: {
          tipo: "CFDI",
          forma_pago: config.esFactura ? header.forma_pago || null : null,
          metodo_pago: config.esFactura ? header.metodo_pago || null : null,
          uso_comprobante: config.esFactura
            ? header.uso_comprobante || null
            : null,
          lugar_expedicion: config.esFactura
            ? header.lugar_expedicion || null
            : null,
          serie_factura: config.esFactura ? header.serie_factura || null : null,
        },
        detalles: totals.lines.map((line, index) =>
          buildDetailPayload({
            line,
            index,
            ivaPct,
            documentId: record?.id,
            folio: record?.folio || 0,
          }),
        ),
        pagos: [],
        cobros: [],
        anticipo: [],
        transferencias: [],
        trans_productos: {},
      },
    };
    try {
      if (record?.id) await actualizarDocumento(record.id, payload);
      else await crearDocumento(payload);
      toast.success(
        record?.id
          ? `${config.singular} ${adjective(
              "actualizado",
              "actualizada",
            )} correctamente.`
          : `${config.singular} ${adjective("creado", "creada")} correctamente.`,
      );
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (selectingPrevious && configuration && !record) {
    return (
      <DocumentoPrevioSelector
        show={show}
        config={config}
        configuration={configuration}
        idEmpresa={header.id_empresa || user.id_empresa}
        onCancel={closePreviousSelector}
        onSelect={setAsDoctoPosterior}
      />
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      fullscreen="md-down"
      centered
      dialogClassName="document-form-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {readOnly
            ? "Consultar"
            : record
              ? "Editar"
              : adjective("Nuevo", "Nueva")}{" "}
          {config.singular.toLowerCase()}
        </Modal.Title>
        {!readOnly && canUsePrevious(config, record, configuration) && (
          <Dropdown align="end" className="ms-auto me-2">
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              className="document-actions-toggle"
              title="Más opciones"
              aria-label="Más opciones"
              disabled={loading || !configuration}
            >
              <EllipsisVertical size={18} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {canUsePrevious(config, record, configuration) && (
                <Dropdown.Item onClick={openPreviousSelector}>
                  Cargar documento previo
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </Modal.Header>
      <Modal.Body className="document-form-modal">
        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <Tabs defaultActiveKey="general" className="document-form-tabs mb-3">
            <Tab eventKey="general" title="Datos generales" className="pt-2">
              <section className="document-field-section ">
                <div className="document-field-heading">
                  <span className="document-field-icon"><Building2 size={18} /></span>
                  <div><h3>Documento y cliente</h3><p>Selecciona la empresa y el cliente; después completa las referencias.</p></div>
                </div>
              <Row className="g-3">
                <Col md={6} xl={5}>
                  <RemoteSelect
                    name="id_empresa"
                    label="Empresa"
                    value={header.id_empresa}
                    onChange={handleCompanyChange}
                    loadOptions={loadCompanies}
                    loadById={obtenerEmpresa}
                    getOptionLabel={(o) =>
                      o.nombre || o.razon_social || `Empresa #${o.id}`
                    }
                    disabled={
                      readOnly ||
                      Boolean(record?.id) ||
                      relatedHeaders.length > 0
                    }
                    required
                  />
                </Col>
                <Col md={6} xl={7}>
                  <RemoteSelect
                    key={`client-${header.id_empresa}`}
                    name="id_cliente"
                    label="Cliente"
                    value={header.id_cliente}
                    onChange={handleClientChange}
                    loadOptions={loadClients}
                    loadById={obtenerClienteDocumento}
                    getOptionLabel={(o) =>
                      `${o.clave ? `${o.clave} — ` : ""}${
                        o.nombre || o.razon_social
                      }`
                    }
                    disabled={
                      readOnly ||
                      !header.id_empresa ||
                      relatedHeaders.length > 0
                    }
                    placeholder={
                      header.id_empresa
                        ? "Selecciona un cliente"
                        : "Selecciona una empresa primero"
                    }
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Fecha *</Form.Label>
                    <Form.Control
                      type="date"
                      value={header.fecha || ""}
                      onChange={(e) => setField("fecha", e.target.value)}
                      disabled={readOnly}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Field
                    label="Referencia"
                    value={header.referencia}
                    onChange={(v) => setField("referencia", v)}
                    disabled={readOnly}
                  />
                </Col>
                <Col md={4}>
                  <Field
                    label="Orden de compra"
                    value={header.orden_compra}
                    onChange={(v) => setField("orden_compra", v)}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
              </section>
              <div className="document-commercial-grid">
              <section className="document-field-section ">
                <div className="document-field-heading">
                  <span className="document-field-icon"><CreditCard size={18} /></span>
                  <div><h3>Condiciones comerciales</h3><p>Moneda, precios y descuento del documento.</p></div>
                </div>
              {isTrue(configuration?.concepto) && <RemoteSelect
                name="id_concepto_presupuesto" label="Concepto presupuesto"
                value={header.id_concepto_presupuesto}
                onChange={setField}
                loadOptions={({search, page, limit}) => listarConceptosPresupuesto({page, limit, filters: [
                  // El backend limita a la empresa actual e incluye conceptos globales.
                  ...(search ? [{property:"descripcion",operator:"like",value:search}] : []),
                ]})}
                loadById={obtenerConceptoPresupuesto}
                getOptionLabel={(option) => option.descripcion}
                disabled={readOnly}
              />}
              <CurrencyFields
                header={header}
                onChange={(name, value) => name === "tipo_cambio" ? changeDocumentCurrency(header.id_moneda, value) : setField(name, value)}
                onCurrencyChange={changeDocumentCurrency}
                disabled={readOnly || !!record?.id || relatedHeaders.length > 0}
              />
              <Row className="g-3">
                <Col md={6}>
                  <RemoteSelect
                    name="id_precio_especial"
                    label="Lista de precio"
                    placeholder="Precio base del producto"
                    value={header.id_precio_especial}
                    onChange={handlePriceListChange}
                    loadOptions={loadPrices}
                    loadById={obtenerPrecioEspecial}
                    getOptionLabel={(o) => o.nombre || o.descripcion}
                    disabled={readOnly}
                  />
                  <small className="text-muted">Sin lista se utiliza el precio base. Una lista seleccionada sustituye ese precio; ambos se convierten a la moneda del documento.</small>
                </Col>
                <Col md={6}>
                  {configuration?.use_totales_desctos !== false &&
                  discountType !== 0 ? (
                    <Field
                      label="Descuento global %"
                      type="number"
                      value={header.descuento_pct}
                      onChange={(v) => setField("descuento_pct", v)}
                      disabled={readOnly}
                    />
                  ) : null}
                </Col>
              </Row>
              </section>
              <section className="document-field-section ">
                <div className="document-field-heading">
                  <span className="document-field-icon"><MapPin size={18} /></span>
                  <div><h3>Operación y entrega</h3><p>Define el envío, su destino y los datos de entrega.</p></div>
                </div>
              <Row className="g-3">
                {config.requiereAlmacen && (
                  <Col md={6}>
                    <RemoteSelect
                      key={`warehouse-${header.id_empresa}`}
                      name="id_almacen"
                      label="Almacén"
                      value={header.id_almacen}
                      onChange={(_name, value) => setField("id_almacen", value)}
                      loadOptions={loadWarehouses}
                      loadById={obtenerAlmacen}
                      getOptionLabel={(o) =>
                        o.nombre || o.descripcion || `Almacén #${o.id}`
                      }
                      disabled={readOnly || !header.id_empresa}
                      required
                    />
                  </Col>
                )}
                {config.usaVehiculo && (
                  <Col md={6}>
                    <RemoteSelect
                      name="id_vehiculos"
                      label="Vehículo"
                      value={header.id_vehiculos}
                      onChange={(_name, value) =>
                        setField("id_vehiculos", value)
                      }
                      loadOptions={loadVehicles}
                      loadById={obtenerVehiculo}
                      getOptionLabel={(o) =>
                        o.nombre ||
                        o.descripcion ||
                        o.placas ||
                        `Vehículo #${o.id}`
                      }
                      disabled={readOnly}
                    />
                  </Col>
                )}
                <Col md={6}>
                  <Field
                    label="Fecha de entrega"
                    type="date"
                    value={header.fecha_entrega}
                    onChange={(v) => setField("fecha_entrega", v)}
                    disabled={readOnly}
                  />
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tipo de envío *</Form.Label>
                    <Form.Select
                      value={header.tipo_envio || ""}
                      onChange={(e) => handleShippingChange(e.target.value)}
                      disabled={
                        readOnly || !header.id_empresa || !header.id_cliente
                      }
                    >
                      <option value="">Selecciona</option>
                      <option>Cliente recoge</option>
                      <option>A Domicilio</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Field
                    label="Persona que requiere"
                    value={header.nombre_solicita}
                    onChange={(v) => setField("nombre_solicita", v)}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12}>
                  <RemoteSelect
                    key={`direction-${header.tipo_envio}-${header.id_empresa}-${header.id_cliente}-${header._empresa_direccion_id}`}
                    name="id_direccion"
                    label="Dirección de envío"
                    value={header.id_direccion}
                    onChange={(_name, value) =>
                      setHeader((current) => ({
                        ...current,
                        id_direccion: value,
                        direccion_envio: value,
                      }))
                    }
                    loadOptions={loadDirections}
                    loadById={obtenerDireccion}
                    getOptionLabel={addressName}
                    disabled={readOnly || !header.tipo_envio}
                    placeholder={
                      header.tipo_envio
                        ? "Selecciona una dirección"
                        : "Selecciona el tipo de envío primero"
                    }
                    required
                  />
                </Col>
                <Col md={6}>
                  <Field
                    label="Horario de entrega"
                    value={header.horario_entrega}
                    onChange={(v) => setField("horario_entrega", v)}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
              </section>
              </div>

              <div className="document-section document-products-section mt-4">
                <div className="document-field-heading"><span className="document-field-icon"><Package size={18} /></span><div><h3>Productos del documento</h3><p>Agrega los conceptos y revisa sus cantidades e importes.</p></div></div>
                <div className="d-flex flex-column flex-md-row align-items-md-end gap-2 mb-3">
                  <div className="flex-grow-1">
                    <RemoteSelect
                      name="producto"
                      label="Producto"
                      value={productId}
                      onChange={(_n, value) => setProductId(value)}
                      loadOptions={loadProducts}
                      loadById={obtenerProductoDocumento}
                      getOptionLabel={(o) =>
                        `${o.clave ? `${o.clave} — ` : ""}${productName(o)}`
                      }
                      disabled={readOnly || !header.id_cliente}
                      placeholder={
                        header.id_cliente
                          ? "Busca un producto"
                          : "Selecciona un cliente primero"
                      }
                    />
                  </div>
                  {!readOnly && configuration?.can_add_products !== false && (
                    <Button onClick={addProduct} disabled={!productId}>
                      <Plus size={17} /> Añadir
                    </Button>
                  )}
                </div>
                <div className="table-responsive">
                  <Table className="document-lines-table" align="middle">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Cantidad</th>
                        <th>Precio</th>
                        <th>
                          {discountType === 0 ? "Descuento" : "Descto. %"}
                        </th>
                        <th>Importe</th>
                        {!readOnly && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {totals.lines.length ? (
                        totals.lines.map((line) => (
                          <tr key={line._key}>
                            <td>
                              <strong>
                                {line.descripcion || productName(line.producto)}
                              </strong>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0.0001"
                                step="any"
                                value={line.cantidad2}
                                onChange={(e) =>
                                  updateLine(
                                    line._key,
                                    "cantidad2",
                                    e.target.value,
                                  )
                                }
                                disabled={
                                  readOnly ||
                                  (config.claveTipo === "REM" &&
                                    (line.ids_detalles || []).length > 1)
                                }
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={line.precio}
                                onChange={(e) =>
                                  updateLine(
                                    line._key,
                                    "precio",
                                    e.target.value,
                                  )
                                }
                                disabled={readOnly}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                max={discountType === 0 ? undefined : "100"}
                                step="0.01"
                                value={
                                  discountType === 0
                                    ? line.descuento || 0
                                    : line.descuento_pct || 0
                                }
                                onChange={(e) =>
                                  updateLine(
                                    line._key,
                                    discountType === 0
                                      ? "descuento"
                                      : "descuento_pct",
                                    e.target.value,
                                  )
                                }
                                disabled={readOnly}
                              />
                            </td>
                            <td className="text-end text-nowrap">
                              {money(line.importe)}
                            </td>
                            {!readOnly && (
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    setLines((current) =>
                                      current.filter(
                                        (item) => item._key !== line._key,
                                      ),
                                    )
                                  }
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={readOnly ? 5 : 6}
                            className="text-center text-secondary py-4"
                          >
                            No hay productos agregados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              <Row className="g-3 mt-1 align-items-start document-summary-row">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Comentarios</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={header.comentarios || ""}
                      onChange={(e) => setField("comentarios", e.target.value)}
                      disabled={readOnly}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <div className="document-totals">
                    <Total label="Subtotal" value={totals.subtotal} />
                    <Total label="Descuento" value={totals.descuento} />
                    {ivaPct > 0 && <Total label="IVA" value={totals.iva} />}
                    {!!totals.ieps && <Total label="IEPS" value={totals.ieps} />}
                    {!!totals.impuestos_adicionales && <Total label="Impuestos adicionales" value={totals.impuestos_adicionales} />}
                    {!!totals.retenciones && <Total label="Retenciones" value={-totals.retenciones} />}
                    {!!totals.retenciones_iva && <Total label="Retenciones IVA" value={-totals.retenciones_iva} />}
                    <Total label="Total" value={totals.importe} strong />
                  </div>
                </Col>
              </Row>
            </Tab>
            {config.esFactura && (
              <Tab
                eventKey="condiciones"
                title="Condiciones de pago"
                className="pt-2"
              >
                <section className="document-field-section">
                <div className="document-field-heading"><span className="document-field-icon"><CreditCard size={18} /></span><div><h3>Calendario de pago</h3><p>Establece el plazo y las fechas de seguimiento de la factura.</p></div></div>
                <Row className="g-3">
                  <Col md={4}>
                    <Field
                      label="Plazo (días)"
                      type="number"
                      value={header.plazo_dias}
                      onChange={(value) => setField("plazo_dias", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col md={4}>
                    <Field
                      label="Fecha de vencimiento"
                      type="date"
                      value={header.fecha_vencimiento}
                      onChange={(value) => setField("fecha_vencimiento", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col md={4}>
                    <Field
                      label="Fecha de revisión"
                      type="date"
                      value={header.fecha_revision}
                      onChange={(value) => setField("fecha_revision", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col xs={12}>
                    <Field
                      label="Condiciones de pago"
                      value={header.condiciones_pago}
                      onChange={(value) => setField("condiciones_pago", value)}
                      disabled={readOnly}
                    />
                  </Col>
                </Row>
                </section>
              </Tab>
            )}
            {config.esFactura && (
              <Tab eventKey="cfdi" title="Facturación (CFDI)" className="pt-2">
                <section className="document-field-section">
                <div className="document-field-heading"><span className="document-field-icon"><FileText size={18} /></span><div><h3>Datos fiscales</h3><p>Verifica emisor y receptor, y completa los datos del comprobante.</p></div></div>
                <Row className="g-3">
                  <Col md={6}>
                    <Field
                      label="Lugar de expedición"
                      value={header.lugar_expedicion}
                      onChange={(value) => setField("lugar_expedicion", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col md={6}>
                    <Field
                      label="Serie"
                      value={header.serie_factura}
                      onChange={(value) => setField("serie_factura", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col md={6}>
                    <FiscalParty
                      title="Emisor"
                      name={header.razon_social_emisor}
                      rfc={header.rfc_emisor}
                      regimen={header.regimen_fiscal_emisor}
                    />
                  </Col>
                  <Col md={6}>
                    <FiscalParty
                      title="Receptor"
                      name={header.razon_social_receptor}
                      rfc={header.rfc_receptor}
                      regimen={header.regimen_fiscal_receptor}
                    />
                  </Col>
                  <Col md={4}>
                    <CatalogSelect
                      label="Forma de pago *"
                      value={header.forma_pago}
                      options={satCatalogs.formas}
                      onChange={(value) => setField("forma_pago", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col md={4}>
                    <CatalogSelect
                      label="Método de pago *"
                      value={header.metodo_pago}
                      options={satCatalogs.metodos}
                      onChange={(value) => setField("metodo_pago", value)}
                      disabled={readOnly}
                    />
                  </Col>
                  <Col md={4}>
                    <CatalogSelect
                      label="Uso de CFDI *"
                      value={header.uso_comprobante}
                      options={satCatalogs.usos}
                      onChange={(value) => setField("uso_comprobante", value)}
                      disabled={readOnly}
                    />
                  </Col>
                </Row>
                </section>
              </Tab>
            )}
          </Tabs>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Cerrar
        </Button>
        {!readOnly && (
          <Button onClick={save} disabled={saving || loading}>
            {saving ? <Spinner size="sm" /> : <Save size={17} />} Guardar{" "}
            {config.singular.toLowerCase()}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function Field({ label, value, onChange, type = "text", disabled }) {
  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </Form.Group>
  );
}
function Total({ label, value, strong }) {
  return (
    <div
      className={`d-flex justify-content-between ${
        strong ? "fw-bold fs-5 border-top pt-2 mt-2" : ""
      }`}
    >
      <span>{label}</span>
      <span>{money(value)}</span>
    </div>
  );
}
function CatalogSelect({ label, value, options, onChange, disabled }) {
  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <Form.Select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        <option value="">Selecciona</option>
        {options.map((option) => (
          <option key={option.id || option.clave} value={option.clave}>
            {option.clave ? `(${option.clave}) ` : ""}
            {option.descripcion || option.nombre}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}
function FiscalParty({ title, name, rfc, regimen }) {
  return (
    <div className="border rounded p-3 h-100">
      <div className="fw-bold text-primary mb-2">{title}</div>
      <div>
        <span className="text-secondary">Razón social:</span>{" "}
        {name || "No identificada"}
      </div>
      <div>
        <span className="text-secondary">RFC:</span> {rfc || "No identificado"}
      </div>
      <div>
        <span className="text-secondary">Régimen fiscal:</span>{" "}
        {regimen || "No identificado"}
      </div>
    </div>
  );
}
